import { dateFormat, dateStampFormat, flatJoin, flatten, round, safeParseDate, zeroPadding } from 'shared/util'
import { KojinAnaliticsResult } from './types'

import {
  DashBoardConfigure,
  HorizontalBarGraphFactory,
  VerticalBarGraphFactory,
  SingleBarGraphFactory,
  ScatterPlotGraphFactory,
  PieGraphFactory,
  MultiPieGraphFactory,
  SunburstGraphFactory,
  TreeMapGraphFactory,
  FunnelGraphFactory,
  //ClusterGraphFactory,
  TimeLineGraphFactory,
  HeatMapGraphFactory
} from 'features/dashBoard'

import { mrpLinkFrom, mrpVisit } from './parse/mrp'
import { lvDuration, lvLinkFrom, lvRefererDomain, lvRefererLinkFrom, lvUserAgentBrowser, lvUserAgentCategory, lvUserAgentOS, lvVisit } from './parse/lv'
import { KojinOrgan } from 'interface/kojin'

const topOrgan = <T>(organs: KojinOrgan[] | undefined, callback: (organ: KojinOrgan) => T): T | undefined => {
  if (!organs) return undefined
  const validOrgans = organs.filter((organ) => organ.sisetuCode !== null)
  if (validOrgans.length === 0) return undefined
  return callback(validOrgans[0])
}

const distinct = <T>(list: T[] | undefined, identifier: (record: T) => string | number): T[] => {
  if (!list || list.length === 0) return []
  const idList = list.map(identifier)
  const distinctIdList = Array.from(new Set(idList))
  return flatten(distinctIdList.map(
    (id) => list.find((record) => identifier(record) === id)
  ))
}

const textDateToAxis = (val: string | null | undefined, ) => {
  return val ? {
    code: dateStampFormat(val),
    name: dateFormat(val)
  } : undefined
}

export const kojinDashBoardConfigure: DashBoardConfigure<KojinAnaliticsResult> = {
  graphs: {
    horizonBar: HorizontalBarGraphFactory,
    verticalBar: VerticalBarGraphFactory,
    singleBar: SingleBarGraphFactory,
    plot: ScatterPlotGraphFactory,
    pie: PieGraphFactory,
    multiPie: MultiPieGraphFactory,
    sunburst: SunburstGraphFactory,
    treemap: TreeMapGraphFactory,
    funnel: FunnelGraphFactory,
    //cluster: ClusterGraphFactory,
    timeline: TimeLineGraphFactory,
    heatMap: HeatMapGraphFactory
  },
  identifier: (kojin) => kojin.kojinCode ? {
    id: kojin.kojinCode,
    name: kojin.isiName ?? kojin.isiNameKana ?? '',
  } : undefined,
  category: {
    target: { title: 'ターゲット', column: 0 },
    journal: { title: '論文', column: 0 },
    careNet: { title: 'ケアネット', column: 0 },
    kojin: { title: '医師属性', column: 1 },
    organ: { title: '所属施設', column: 2 },
    mrp: { title: 'MRP', column: 3 },
    lv: { title: 'Web講演会', column: 3 }
  },
  targetList: {
    category: (kojin, sequence) => {
      return kojin.target?.category?.find((category) => category.sequence === sequence)
    },
    score: (kojin, sequence) => {
      return kojin.target?.score?.find((score) => score.sequence === sequence)?.score
    }
  },
  axis: {
    targetType: {
      title: '指定分類',
      isRank: true,
      category: 'target',
      val: (kojin) => kojin.target?.targetType ? {
        code: kojin.target.targetType.code.toString(),
        name: kojin.target.targetType.name ?? '',
      } : {
        code: '-',
        name: 'なし'
      }
    },

    sourceType: {
      title: '元/拡張',
      category: 'target',
      val: (kojin) => kojin.target ? {
        code: kojin.target?.sourceType === 0 ? '0' : '1',
        name: kojin.target?.sourceType === 0 ? '元' : '拡張',
      } : {
        code: '-',
        name: 'なし'
      }
    },
    rate: {
      title: '確度',
      category: 'target',
      val: (kojin) => (kojin.target?.sourceType === 1 && kojin.target?.rate) ? {
        code: zeroPadding(Math.ceil(kojin.target.rate * 1000), 6),
        name: (Math.ceil(kojin.target.rate * 1000) / 10).toString() + '%',
      } : {
        code: '-',
        name: 'なし'
      }
    },
    seibetu: {
      title: '性別',
      category: 'kojin',
      val: (kojin) => kojin.seibetuCode ? {
        code: kojin.seibetuCode,
        name: kojin.seibetuCode === '1' ? '男性' : kojin.seibetuCode === '2' ? '女性' : '不明'
      } : undefined,
    },
    nenrei: {
      title: '年齢',
      category: 'kojin',
      val: (kojin) => kojin.nenrei ? (
        kojin.nenrei < 20 ? { code: '10', name: '10代以下' } :
        kojin.nenrei < 30 ? { code: '20', name: '20代' } :
        kojin.nenrei < 40 ? { code: '30', name: '30代' } :
        kojin.nenrei < 50 ? { code: '40', name: '40代' } :
        kojin.nenrei < 60 ? { code: '50', name: '50代' } :
        kojin.nenrei < 70 ? { code: '60', name: '60代' } :
        kojin.nenrei < 80 ? { code: '70', name: '70代' } : { code: '80', name: '80歳以上' }
      ) : undefined
    },
    birthDate: {
      title: '生年月日',
      category: 'kojin',
      val: (kojin) => textDateToAxis(kojin.seinenGappi),
      time: (kojin) => safeParseDate(kojin.seinenGappi)
    },
    syussinchi: {
      title: '出身地',
      category: 'kojin',
      val: (kojin) => kojin.syussinchiCode ? {
        code: kojin.syussinchiCode,
        name: kojin.syussinchiCode ?? undefined,
      } : undefined
    },
    sotukou: {
      title: '卒業校',
      category: 'kojin',
      val: (kojin) => kojin.sotukouCode && kojin.sotukouCode !== '799' ? {
        code: kojin.sotukouCode,
        name: kojin.sotukouName ?? '',
      } : undefined
    },
    gakubu: {
      title: '学部',
      category: 'kojin',
      val: (kojin) => kojin.gakubuCode ? {
        code: kojin.gakubuCode,
        name: kojin.gakubuName ?? '',
      } : undefined
    },
    sotugyouYear: {
      title: '卒業年度',
      category: 'kojin',
      val: (kojin) => kojin.sotugyouYear ? {
        code: `${kojin.sotugyouYear}`,
        name: `${kojin.sotugyouYear}年`,
      } : undefined
    },
    isikai: {
      title: '医師会',
      category: 'kojin',
      val: (kojin) => kojin.isikaiCode && kojin.isikaiCode !== '99' ? {
        code: kojin.isikaiCode,
        name: kojin.isikaiName ?? '',
      } : undefined
    },
    kaikin: {
      title: '開業/勤務医',
      category: 'kojin',
      val: (kojin) => kojin.kaikinCode ? {
        code: kojin.kaikinCode,
        name: kojin.kaikinCode === '1' ? '開業医' : kojin.kaikinCode === '2' ? '勤務医' : '不明',
      } : undefined
    },
    sinryoka: {
      title: '診療科',
      category: 'kojin',
      val: (kojin) => kojin.sinryoka?.map(
        (sinryoka) => ({
          code: sinryoka.sinryokaCode,
          name: sinryoka.sinryokaName,
        })
      )
    },
    gakkai: {
      title: '所属学会',
      category: 'kojin',
      val: (kojin) => kojin.gakkai?.map(
        (gakkai) => ({
          code: gakkai.gakkaiCode,
          name: gakkai.gakkaiName,
        })
      )
    },
    gakkaiCount: {
      title: '所属学会数',
      category: 'kojin',
      val: (kojin) => kojin.gakkai ? {
        code: zeroPadding(kojin.gakkai.length, 10),
        name: `${kojin.gakkai.length}件`
      } : {
        code: '0000000000',
        name: '0件'
      }
    },
    senmoni: {
      title: '専門医',
      category: 'kojin',
      val: (kojin) => kojin.senmoni?.map(
        (senmoni) => ({
          code: senmoni.senmoniCode,
          name: senmoni.senmoniName,
        })
      )
    },
    senmoniCount: {
      title: '専門医資格数',
      category: 'kojin',
      val: (kojin) => kojin.senmoni ? {
        code: zeroPadding(kojin.senmoni.length, 10),
        name: `${kojin.senmoni.length}件`,
      } : {
        code: '0',
        name: '0件',
      }
    },
    yakusyoku: {
      title: '役職',
      category: 'kojin',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => organ.yakusyokuCode !== null
        ).map(
          (organ) => ({
            code: organ.yakusyokuCode ?? '',
            name: organ.yakusyokuName ?? ''
          })
        ),
        (record) => record.code
      )
    },
    buka: {
      title: '部課',
      category: 'kojin',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => organ.bukaCode !== null
        ).map(
          (organ) => ({
            code: organ.bukaCode ?? '',
            name: organ.bukaName ?? ''
          })
        ),
        (record) => record.code
      )
    },
    bukaSinryokaCategory: {
      title: '部課診療科(大項目)',
      category: 'kojin',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => organ.bukaHojoCategoryCode
        ).map(
          (organ) => ({
            code: organ.bukaHojoCategoryCode ?? '',
            name: organ.bukaHojoCategoryName ?? ''
          })
        ),
        (record) => record.code
      )
    },
    bukaSinryoka: {
      title: '部課診療科(詳細)',
      category: 'kojin',
      categoryTitle: '大項目',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => organ.bukaHojoCategoryCode && organ.bukaHojoCode
        ).map(
          (organ) => ({
            code: organ.bukaHojoCode ?? '',
            name: organ.bukaHojoName ?? '',
            category: {
              code: organ.bukaHojoCategoryCode ?? '',
              name: organ.bukaHojoCategoryName ?? ''
            }
          })
        ),
        (record) => record.code
      )
    },
    sisetu: {
      title: '所属施設',
      category: 'organ',
      val: (kojin) => kojin.organ?.filter(
        (organ) => organ.sisetuCode !== null
      ).map(
        (organ) => ({
          code: organ.sisetuCode ?? '',
          name: organ.sisetuName ?? '',
        })
      ) || undefined,
    },
    primarySisetu: {
      title: '所属施設(病床数最大)',
      category: 'organ',
      val: (kojin) => topOrgan(kojin.organ, (organ) => ({
        code: organ.sisetuCode ?? '',
        name: organ.sisetuName ?? ''
      }))
    },
    sisetuCount: {
      title: '所属施設数',
      category: 'organ',
      val: (kojin) => kojin.organ ? {
        code: zeroPadding(kojin.organ.length, 5),
        name: `${kojin.organ.length}施設`,
      } : {
        code: '0',
        name: '0施設',
      }
    },
    block: {
      title: 'ブロック',
      category: 'organ',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => (organ.sisetuBlockCode ? true : false)
        ).map(
          (organ) => ({
            code: organ.sisetuBlockCode ?? '',
            name: organ.sisetuBlockName
          })
        ),
        (record) => record.code
      )
    },
    todoufuken: {
      title: '都道府県',
      category: 'organ',
      categoryTitle: 'ブロック',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => (organ.sisetuKenCode ? true : false)
        ).map(
          (organ) => ({
            code: organ.sisetuKenCode ?? '',
            name: organ.sisetuKenName ?? '',
            category: organ.sisetuBlockCode ? {
              code: organ.sisetuBlockCode ?? '',
              name: organ.sisetuBlockName ?? '',
            } : undefined
          })
        ),
        (record) => record.code
      )
    },
    topTodoufuken: {
      title: '都道府県(病床数最大)',
      category: 'organ',
      categoryTitle: 'ブロック',
      val: (kojin) => topOrgan(kojin.organ, (organ) => ({
        code: organ.sisetuKenCode ?? '',
        name: organ.sisetuKenName ?? '',
        category: organ.sisetuBlockCode ? {
          code: organ.sisetuBlockCode ?? '',
          name: organ.sisetuBlockName ?? '',
        } : undefined
      }))
    },
    keieitaiCategory: {
      title: '経営体(大項目)',
      category: 'organ',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => (organ.keieitai ? true : false)
        ).map(
          (organ) => ({
            code: organ.keieitai?.categoryCode ?? '',
            name: organ.keieitai?.categoryName
          })
        ),
        (record) => record.code
      )
    },
    keieitai: {
      title: '経営体(詳細)',
      category: 'organ',
      categoryTitle: '区分',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => (organ.keieitai ? true : false)
        ).map(
          (organ) => ({
            code: organ.keieitai?.code ?? '',
            name: organ.keieitai?.name ?? '',
            category: organ.keieitai?.categoryCode ? {
              code: organ.keieitai.categoryCode,
              name: organ.keieitai.categoryName,
            } : undefined
          })
        ),
        (record) => record.code
      )
    },
    sisetuKubunCategory: {
      title: '施設区分(大項目)',
      category: 'organ',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => (organ.sisetuKubun?.categoryCode ? true : false)
        ).map(
          (organ) => ({
            code: organ.sisetuKubun?.categoryCode ?? '',
            name: organ.sisetuKubun?.categoryName
          })
        ),
        (record) => record.code
      )
    },
    sisetuKubun: {
      title: '施設区分(詳細)',
      category: 'organ',
      categoryTitle: '大項目',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => (organ.sisetuKubun ? true : false)
        ).map(
          (organ) => ({
            code: organ.sisetuKubun?.code ?? '',
            name: organ.sisetuKubun?.name,
            category: organ.sisetuKubun?.categoryCode ? {
              code: organ.sisetuKubun?.categoryCode,
              name: organ.sisetuKubun?.categoryName,
            } : undefined
          })
        ),
        (record) => record.code
      )
    },
    byouinSyubetu: {
      title: '病院種別',
      category: 'organ',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => (organ.byouinSyubetu ? true : false)
        ).map(
          (organ) => ({
            code: organ.byouinSyubetu?.code ?? '',
            name: organ.byouinSyubetu?.shortName ?? organ.byouinSyubetu?.name,
          })
        ),
        (record) => record.code
      )
    },
    bedCount: {
      title: '病床数',
      category: 'organ',
      val: (kojin) => distinct(
        kojin.organ?.filter(
          (organ) => (organ.bedCount !== null ? true : false)
        ).map(
          (organ) =>
            (organ.bedCount === null || organ.bedCount === undefined) ? { code: '000', name: '無床' } :
            organ.bedCount >= 600 ? { code: '600', name: '600床以上' } :
            organ.bedCount >= 400 ? { code: '400', name: '400～599床' } :
            organ.bedCount >= 200 ? { code: '200', name: '200～399床' } :
            organ.bedCount >= 100 ? { code: '100', name: '100～199床' } :
            organ.bedCount >= 20 ? { code: '020', name: '20～99床' } :
            organ.bedCount > 0 ? { code: '001', name: '有床～19床' } : { code: '000', name: '無床' }
        ) || undefined,
        (record) => record.code
      )
    },
    maxBedCount: {
      title: '最大病床数',
      category: 'organ',
      val: (kojin) => topOrgan(kojin.organ, (organ) =>
          (organ.bedCount === null || organ.bedCount === undefined) ? { code: 'N', name: '無床' } :
          organ.bedCount >= 600 ? { code: '1', name: '600床以上' } :
          organ.bedCount >= 400 ? { code: '2', name: '400～599床' } :
          organ.bedCount >= 200 ? { code: '3', name: '200～399床' } :
          organ.bedCount >= 100 ? { code: '4', name: '100～199床' } :
          organ.bedCount >= 20 ? { code: '5', name: '20～99床' } :
          organ.bedCount > 0 ? { code: '6', name: '有床～19床' } : { code: 'N', name: '無床' }
      )
    },

    journalScore: {
      title: '論文ランク',
      isRank: true,
      category: 'journal',
      val: (kojin) =>
        kojin.score?.journal?.score ? (
          kojin.score.journal.score >= 20 ? { code: '04', name: '20pt' } :
          kojin.score.journal.score >= 8 ? { code: '06', name: '8pt' } :
          kojin.score.journal.score >= 5 ? { code: '08', name: '5pt' } :
          kojin.score.journal.score >= 3 ? { code: '09', name: '3pt' } :
          kojin.score.journal.score >= 1 ? { code: '10', name: '1pt' } : { code: '11', name: '0pt' }
        ) : { code: '11', name: '0pt' }
    },
    journalCount: {
      title: '論文数',
      category: 'journal',
      val: (kojin) =>
        kojin.score?.journal?.counts ? (
          kojin.score.journal.counts.total >= 100 ? { code: '100', name: '100以上' } :
          kojin.score.journal.counts.total >= 80 ? { code: '080', name: '80以上' } :
          kojin.score.journal.counts.total >= 50 ? { code: '050', name: '50以上' } :
          kojin.score.journal.counts.total >= 30 ? { code: '030', name: '30以上' } :
          kojin.score.journal.counts.total >= 10 ? { code: '010', name: '10以上' } :
          kojin.score.journal.counts.total >= 5 ? { code: '005', name: '5以上' } :
          kojin.score.journal.counts.total >= 4 ? { code: '004', name: '4' } :
          kojin.score.journal.counts.total >= 3 ? { code: '003', name: '3' } :
          kojin.score.journal.counts.total >= 2 ? { code: '002', name: '2' } :
          kojin.score.journal.counts.total >= 1 ? { code: '001', name: '1' } : { code: '000', name: '0' }
        ) : undefined
    },
    journalFirst: {
      title: '論文数(先頭)',
      category: 'journal',
      val: (kojin) =>
        kojin.score?.journal?.counts ? (
          kojin.score.journal.counts.first >= 100 ? { code: '100', name: '100以上' } :
          kojin.score.journal.counts.first >= 80 ? { code: '080', name: '80以上' } :
          kojin.score.journal.counts.first >= 50 ? { code: '050', name: '50以上' } :
          kojin.score.journal.counts.first >= 30 ? { code: '030', name: '30以上' } :
          kojin.score.journal.counts.first >= 10 ? { code: '010', name: '10以上' } :
          kojin.score.journal.counts.first >= 5 ? { code: '005', name: '5以上' } :
          kojin.score.journal.counts.first >= 4 ? { code: '004', name: '4' } :
          kojin.score.journal.counts.first >= 3 ? { code: '003', name: '3' } :
          kojin.score.journal.counts.first >= 2 ? { code: '002', name: '2' } :
          kojin.score.journal.counts.first >= 1 ? { code: '001', name: '1' } : { code: '000', name: '0' }
        ) : undefined
    },
    journalLast: {
      title: '論文数(責任)',
      category: 'journal',
      val: (kojin) =>
        kojin.score?.journal?.counts ? (
          kojin.score.journal.counts.last >= 100 ? { code: '100', name: '100以上' } :
          kojin.score.journal.counts.last >= 80 ? { code: '080', name: '80以上' } :
          kojin.score.journal.counts.last >= 50 ? { code: '050', name: '50以上' } :
          kojin.score.journal.counts.last >= 30 ? { code: '030', name: '30以上' } :
          kojin.score.journal.counts.last >= 10 ? { code: '010', name: '10以上' } :
          kojin.score.journal.counts.last >= 5 ? { code: '005', name: '5以上' } :
          kojin.score.journal.counts.last >= 4 ? { code: '004', name: '4' } :
          kojin.score.journal.counts.last >= 3 ? { code: '003', name: '3' } :
          kojin.score.journal.counts.last >= 2 ? { code: '002', name: '2' } :
          kojin.score.journal.counts.last >= 1 ? { code: '001', name: '1' } : { code: '000', name: '0' }
        ) : undefined
    },

    disease: {
      title: '関心疾患',
      category: 'careNet',
      val: (kojin) => distinct(
        kojin.diseases?.map(
          (disease) => ({
            code: disease.icd10code,
            name: disease.nodeName || 'unknown',
          })
        ) || undefined,
        (record) => record.code
      )
    },
    cnUserType: {
      title: 'CN会員',
      category: 'careNet',
      val: (kojin) => kojin.personalId ? { code: '0', name: '会員' } : { code: '1', name: '非会員' }
    },
    actorStats: {
      title: 'CN出演歴',
      category: 'careNet',
      val: (kojin) =>
        kojin.actor ? (
          kojin.actor.isComActor ? (
            kojin.actor.isTvActor ? (
              kojin.actor.isDvdActor ? { code: '0', name: 'COM/TV/DVD' } : { code: '1', name: 'COM/TV' }
            ) :
            kojin.actor.isDvdActor ? { code: '2', name: 'COM/DVD' } : { code: '3', name: 'COM' }
          ) :
          kojin.actor.isTvActor ? (
            kojin.actor.isDvdActor ? { code: '4', name: 'TV/DVD' } : { code: '5', name: 'TV' }
          ) :
          kojin.actor.isDvdActor ? { code: '6', name: 'DVD' } : undefined
        ) : undefined
    },
    picksExpert: {
      title: 'DrsPicks',
      category: 'careNet',
      val: (kojin) => kojin.picksExpert ? { code: '1', name: 'Expert' } : { code: '0', name: 'None' }
    },
    mailStatus: {
      title: 'メール受信可否',
      category: 'careNet',
      val: (kojin) => kojin.mailStatus === 'OK' ? { code: '1_OK', name: '許諾' } :
        kojin.mailStatus === 'REJECT' ? { code: '2_REJECT', name: '拒否' } :
        kojin.mailStatus === 'EMPTY' ? { code: '3_EMPTY', name: '空' } :
        { code: '4_UNKNOWN', name: '不明' }
    },
    kanriBunrui: {
      title: '勤務先分類',
      category: 'careNet',
      val: (kojin) => 
        kojin.kanriBunrui === 'D01' ? { code: 'D01', name: 'D01:診療所 経営者' } :
        kojin.kanriBunrui === 'D02' ? { code: 'D02', name: 'D02:診療所 勤務医' } :
        kojin.kanriBunrui === 'D03' ? { code: 'D03', name: 'D03:小規模病院' } : 
        kojin.kanriBunrui === 'D04' ? { code: 'D04', name: 'D04:中規模病院' } :
        kojin.kanriBunrui === 'D05' ? { code: 'D05', name: 'D05:大病院' } :
        kojin.kanriBunrui === 'D06' ? { code: 'D06', name: 'D06:大規模病院' } :
        kojin.kanriBunrui === 'D07' ? { code: 'D07', name: 'D07:その他' } :
        kojin.kanriBunrui === 'P01' ? { code: 'P01', name: 'P01:病院 薬剤師' } :
        kojin.kanriBunrui === 'P02' ? { code: 'P02', name: 'P02:診療所 薬剤師' } :
        kojin.kanriBunrui === 'P03' ? { code: 'P03', name: 'P03:調剤薬局 薬剤師' } :
        kojin.kanriBunrui === 'P04' ? { code: 'P04', name: 'P04:その他 薬剤師' } :
        { code: 'Z', name: '不明' }
    },
    primarySinryoka: {
      title: 'COM診療科',
      category: 'careNet',
      val: (kojin) => kojin.primarySinryoka ? {
        code: kojin.primarySinryoka.sinryokaCode,
        name: kojin.primarySinryoka.sinryokaName ?? ''
      } : undefined
    },
    /*
    comSinryoka: {
      title: 'COM診療科',
      category: 'careNet',
      val: (kojin) => kojin.comSinryoka?.map(
        (sinryoka) => ({
          code: sinryoka.sinryokaCode,
          name: sinryoka.sinryokaName ?? ''
        })
      )
    },
    */

    mrpUserRank: {
      title: 'Tierランク',
      isRank: true,
      category: 'mrp',
      val: (kojin) => kojin.mrpStats?.rank
    },
    mrpEvaluationAverage: {
      title: 'MRP/平均評価(年間)',
      category: 'mrp',
      val: (kojin) => kojin.mrpStats?.evaluationAverage ? {
        code: zeroPadding(Math.floor(kojin.mrpStats.evaluationAverage), 6),
        name: Math.floor(kojin.mrpStats.evaluationAverage).toString() + '点代'
      } : undefined
    },
    mrpVisit: {
      title: 'MRP/視聴状態',
      isRank: true,
      source: 'mrp',
      category: 'mrp',
      val: (kojin) => mrpVisit(kojin.mrp),
      valByMrpLog: (log) => mrpVisit(log)
    },
    mrpLinkFrom: {
      title: 'MRP/LinkFrom',
      source: 'mrp',
      category: 'mrp',
      val: (kojin) => mrpLinkFrom(kojin.mrp),
      valByMrpLog: (log) => mrpLinkFrom(log)
    },
    mrpVisitDate: {
      title: 'MRP/視聴日',
      source: 'mrp',
      category: 'mrp',
      val: (kojin) => textDateToAxis(kojin.mrp?.firstviewdate),
      valByMrpLog: (log) => textDateToAxis(log.firstviewdate),
      time: (kojin) => safeParseDate(kojin.mrp?.firstviewdate),
      byMrpLog: (log) => safeParseDate(log.firstviewdate)
    },

    lvVisit: {
      title: 'Web講演会/視聴状態',
      isRank: true,
      source: 'lv',
      category: 'lv',
      val: (kojin) => lvVisit(kojin.lv),
      valByLvLog: (log) => lvVisit(log)
    },
    lvDuration: {
      title: 'Web講演会/視聴時間',
      isRank: true,
      source: 'lv',
      category: 'lv',
      val: (kojin) => lvDuration(kojin.lv),
      valByLvLog: (log) => lvDuration(log)
    },
    lvUserAgentCategory: {
      title: 'Web講演会/環境',
      source: 'lv',
      category: 'lv',
      val: (kojin) => lvUserAgentCategory(kojin.lv),
      valByLvLog: (log) => lvUserAgentCategory(log)
    },
    lvUserAgentOS: {
      title: 'Web講演会/OS',
      source: 'lv',
      category: 'lv',
      val: (kojin) => lvUserAgentOS(kojin.lv),
      valByLvLog: (log) => lvUserAgentOS(log)
    },
    lvUserAgentBrowser: {
      title: 'Web講演会/Browser',
      source: 'lv',
      category: 'lv',
      val: (kojin) => lvUserAgentBrowser(kojin.lv),
      valByLvLog: (log) => lvUserAgentBrowser(log)
    },
    lvRefererDomain: {
      title: 'Web講演会/リファラ',
      source: 'lv',
      category: 'lv',
      val: (kojin) => lvRefererDomain(kojin.lv),
      valByLvLog: (log) => lvRefererDomain(log)
    },
    lvRefererLinkFrom: {
      title: 'Web講演会/LinkFrom(リファラ)',
      source: 'lv',
      category: 'lv',
      val: (kojin) => lvRefererLinkFrom(kojin.lv),
      valByLvLog: (log) => lvRefererLinkFrom(log)
    },
    lvLinkFrom: {
      title: 'Web講演会/LinkFrom(単一)',
      source: 'lv',
      category: 'lv',
      val: (kojin) => lvLinkFrom(kojin.lv),
      valByLvLog: (log) => lvLinkFrom(log)
    },
    lvReservation: {
      title: 'Web講演会/予約日',
      source: 'lv',
      category: 'lv',
      val: (kojin) => textDateToAxis(kojin.lv?.reserveDate),
      valByLvLog: (log) => textDateToAxis(log.reserveDate),
      time: (kojin) => safeParseDate(kojin.lv?.reserveDate),
      byLvLog: (log) => safeParseDate(log.reserveDate)
    },
    lvBroadcastDate: {
      title: 'Web講演会/視聴日',
      category: 'lv',
      source: 'lv',
      val: (kojin) => textDateToAxis(kojin.lv?.broadcastDate),
      valByLvLog: (log) => textDateToAxis(log.broadcastDate),
      time: (kojin) => safeParseDate(kojin.lv?.broadcastDate),
      byLvLog: (log) => safeParseDate(log.broadcastDate)
    }
  },
  value: {
    count: {
      title: '医師数',
      val: () => 1,
      unit: '人',
    },

    rate: {
      title: '確度',
      category: 'target',
      val: (kojin) => kojin.target?.sourceType === 1 ? kojin.target.rate * 100 : undefined,
      unit: '%'
    },

    nenrei: {
      title: '年齢',
      category: 'kojin',
      val: (kojin) => kojin.nenrei ?? undefined,
      unit: '歳'
    },
    yakusyoku: {
      title: '役職ランク',
      category: 'kojin',
      val: (kojin) => kojin.organ?.reduce<number>(
        (s, organ) => Math.max(s, 9999 - (organ.positionRank ?? 9999)),
        0
      ),
      label: (kojin) => kojin.organ?.reduce<{ rank: number; label: string } | undefined>(
        (s, organ) => !s || s.rank < (organ.positionRank ?? 0) ? {
          rank: organ.positionRank ?? 0,
          label: organ.yakusyokuName ?? ''
        } : s,
        undefined
      )?.label
    },
    gakkaiCount: {
      title: '所属学会数',
      category: 'kojin',
      val: (kojin) => kojin.gakkai?.length ?? 0,
      unit: '件'
    },
    senmoniCount: {
      title: '専門医資格数',
      category: 'kojin',
      val: (kojin) => kojin.senmoni?.length ?? 0,
      unit: '件'
    },
    sisetuCount: {
      title: '所属施設数',
      category: 'kojin',
      val: (kojin) => kojin.organ?.length ?? 0,
      unit: '施設'
    },

    bedCount: {
      title: '病床数',
      category: 'organ',
      val: (kojin) => kojin.organ ? kojin.organ.reduce<number>(
        (s, organ) => Math.max(s, organ.bedCount ?? 0),
        0
      ) : undefined,
      unit: '床'
    },
    maxBedCount: {
      title: '最大病床数',
      category: 'organ',
      val: (kojin) => topOrgan(kojin.organ, (organ) => organ.bedCount ?? 0),
      unit: '床'
    },

    articleCount: {
      title: '論文数',
      category: 'journal',
      val: (kojin) => kojin.score?.journal?.counts.total ?? undefined,
      minValue: 0,
      label: (kojin) => kojin.score?.journal ? flatJoin(' ',
        `${kojin.score.journal.counts.first}件`,
        kojin.score.journal.counts.first > 0 ? `先頭: ${kojin.score.journal.counts.first}件` : undefined,
        kojin.score.journal.counts.second > 0 ? `第二: ${kojin.score.journal.counts.second}件` : undefined,
        kojin.score.journal.counts.last > 0 ? `責任: ${kojin.score.journal.counts.last}件` : undefined,
        `全国: ${kojin.score.journal.rank}位`
      ) : undefined
    },
    articleFirstCount: {
      title: '論文数(先頭)',
      category: 'journal',
      val: (kojin) => kojin.score?.journal?.counts?.first ?? undefined,
      minValue: 0,
      unit: '件'
    },
    articleLastCount: {
      title: '論文数(責任)',
      category: 'journal',
      val: (kojin) => kojin.score?.journal?.counts?.last ?? undefined,
      minValue: 0,
      unit: '件'
    },

    journalScore: {
      title: '論文スコア',
      category: 'journal',
      val: (kojin) => kojin.score?.journal?.score ?? undefined,
      minValue: 0,
      unit: 'pt'
    },

    dpcScore: {
      title: 'DPCスコア',
      category: 'journal',
      val: (kojin) => kojin.score?.dpc?.score ?? undefined,
      minValue: 0,
      unit: 'pt'
    },

    cancerScore: {
      title: 'がんスコア',
      category: 'journal',
      val: (kojin) => kojin.score?.cancer?.score ?? undefined,
      minValue: 0,
      unit: 'pt'
    },

    mrpAssignCount: {
      title: 'MRP/ターゲット回数(年間)',
      category: 'mrp',
      val: (kojin) => kojin.mrpStats?.assignCount ?? undefined,
//    minValue: 0,
      unit: '回'
    },
    mrpVisitCount: {
      title: 'MRP/アクセス回数(年間)',
      category: 'mrp',
      val: (kojin) => kojin.mrpStats?.visitCount ?? undefined,
//    minValue: 0,
      unit: '回'
    },
    mrpVisitRate: {
      title: 'MRP/アクセス率(年間)',
      category: 'mrp',
      val: (kojin) => round(kojin.mrpStats?.visitRate ?? 0, 2) ?? undefined,
      minValue: 0,
      unit: '%'
    },
    mrpEvaluationCount: {
      title: 'MRP/評価回数(年間)',
      category: 'mrp',
      val: (kojin) => kojin.mrpStats?.evaluationCount ?? undefined,
      minValue: 0,
      unit: '回'
    },
    mrpEvaluationAverage: {
      title: 'MRP/平均評価(年間)',
      category: 'mrp',
      val: (kojin) => kojin.mrpStats?.evaluationAverage ?? undefined,
      minValue: 1,
      maxValue: 5,
      unit: '点'
    },

    lvDuration: {
      title: 'Web講演会/視聴時間',
      category: 'lv',
      source: 'lv',
      val: (kojin) => {
        return kojin.lv?.duration ?? undefined
      }
    }
  }
}
